import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Grid3X3, Target, Zap, MessageCircle } from "lucide-react";

interface MessagingMatrixCardProps {
  messagingVariant: string | null;
  messagingAngle: string | null;
  roleTypeUsed: string | null;
  accountCategory: string | null;
  matrixSource: string | null;
  enrichmentContext?: Record<string, any> | null;
}

export function MessagingMatrixCard({
  messagingVariant,
  messagingAngle,
  roleTypeUsed,
  accountCategory,
  matrixSource,
  enrichmentContext,
}: MessagingMatrixCardProps) {
  const painPoints = enrichmentContext?.pain_points as string[] | undefined;
  const valueProps = enrichmentContext?.value_propositions as string[] | undefined;
  const hookType = enrichmentContext?.hook_type as string | undefined;

  const hasContent = messagingVariant || messagingAngle || roleTypeUsed || accountCategory;
  if (!hasContent) return null;

  return (
    <Card className="border-dashed">
      <CardHeader className="pb-2 pt-3 px-4">
        <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
          <Grid3X3 className="h-3.5 w-3.5" />
          Messaging Matrix
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-3 space-y-2.5">
        <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
          {messagingVariant && (
            <div>
              <span className="text-xs text-muted-foreground">Variant</span>
              <div className="font-medium">{formatVariant(messagingVariant)}</div>
            </div>
          )}
          {messagingAngle && (
            <div>
              <span className="text-xs text-muted-foreground">Angle</span>
              <div className="font-medium">{messagingAngle}</div>
            </div>
          )}
          {roleTypeUsed && (
            <div>
              <span className="text-xs text-muted-foreground">Role Type</span>
              <div className="font-medium">{roleTypeUsed}</div>
            </div>
          )}
          {accountCategory && (
            <div>
              <span className="text-xs text-muted-foreground">Account Category</span>
              <div className="font-medium">{accountCategory}</div>
            </div>
          )}
          {matrixSource && (
            <div>
              <span className="text-xs text-muted-foreground">Matrix Source</span>
              <Badge variant="outline" className="text-[11px]">{matrixSource}</Badge>
            </div>
          )}
          {hookType && (
            <div>
              <span className="text-xs text-muted-foreground">Hook Type</span>
              <div className="font-medium flex items-center gap-1">
                <Zap className="h-3 w-3 text-amber-500" />
                {hookType}
              </div>
            </div>
          )}
        </div>

        {painPoints && painPoints.length > 0 && (
          <div>
            <span className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
              <Target className="h-3 w-3" />
              Pain Points Addressed
            </span>
            <div className="flex flex-wrap gap-1">
              {painPoints.map((pp, i) => (
                <Badge key={i} variant="secondary" className="text-[11px]">
                  {pp}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {valueProps && valueProps.length > 0 && (
          <div>
            <span className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
              <MessageCircle className="h-3 w-3" />
              Value Props Used
            </span>
            <div className="flex flex-wrap gap-1">
              {valueProps.map((vp, i) => (
                <Badge key={i} variant="outline" className="text-[11px] bg-blue-50 text-blue-700 border-blue-200">
                  {vp}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function formatVariant(variant: string): string {
  return variant
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
